import React, { useState } from 'react';
import { RobotOutlined, InfoCircleOutlined, BulbOutlined, CheckCircleOutlined } from '@ant-design/icons';

interface AITemplateGeneratorStepProps {
  config: any;
  setConfig: (config: any) => void;
}

const AITemplateGeneratorStep: React.FC<AITemplateGeneratorStepProps> = ({ config, setConfig }) => {
  const [description, setDescription] = useState(config.aiDescription || '');
  const [style, setStyle] = useState(config.aiStyle || 'modern');
  const [colors, setColors] = useState(config.aiColors || 'blue');

  // Ejemplos de descripciones
  const examples = [
    {
      title: 'Landing Page - SaaS',
      description: 'Landing page moderna para software SaaS. Hero section con CTA prominente, sección de features con iconos, pricing table con 3 planes, testimonios de clientes, y footer con enlaces.'
    },
    {
      title: 'Portfolio Personal',
      description: 'Portfolio minimalista para diseñador. Header con navegación sticky, hero section con foto, galería de proyectos en grid, sección about me, skills con progress bars, y formulario de contacto.'
    },
    {
      title: 'Restaurante',
      description: 'Sitio web para restaurante elegante. Hero con imagen de comida, sección de menú por categorías (entradas, platos fuertes, postres), galería de fotos del local, horarios de atención, mapa de ubicación.'
    },
    {
      title: 'E-commerce',
      description: 'Tienda online de ropa. Hero con slider de ofertas, grid de productos destacados con hover effects, filtros por categoría, carrito de compras flotante, footer con redes sociales y newsletter.'
    }
  ];

  const handleUseExample = (example: typeof examples[0]) => {
    setDescription(example.description);
    setConfig({
      ...config,
      aiDescription: example.description,
      aiStyle: style,
      aiColors: colors
    });
  };

  // Actualizar config en tiempo real
  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setConfig({
      ...config,
      aiDescription: value,
      aiStyle: style,
      aiColors: colors
    });
  };

  const handleStyleChange = (value: string) => {
    setStyle(value);
    setConfig({
      ...config,
      aiDescription: description,
      aiStyle: value,
      aiColors: colors
    });
  };

  const handleColorsChange = (value: string) => {
    setColors(value);
    setConfig({
      ...config,
      aiDescription: description,
      aiStyle: style,
      aiColors: value
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg flex items-center justify-center">
            <RobotOutlined className="text-2xl text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Describe tu sitio web</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Powered by Amazon Bedrock (Claude 3.5 Sonnet)</p>
          </div>
        </div>
        <p className="text-sm text-purple-800 dark:text-purple-200">
          Describe detalladamente cómo quieres que sea tu sitio. Después de confirmar el pago, 
          nuestra IA generará tu sitio web completo en minutos.
        </p>
      </div>

      {/* Ejemplos rápidos */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BulbOutlined className="text-yellow-600 dark:text-yellow-400" />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Ejemplos rápidos (click para usar)
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {examples.map((example, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleUseExample(example)}
              className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-left hover:border-purple-400 dark:hover:border-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-all"
            >
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                {example.title}
              </h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                {example.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* Descripción */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Describe tu sitio web *
        </label>
        <textarea
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder="Sé específico sobre las secciones, contenido, y funcionalidades que quieres..."
          rows={8}
          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
        />
        <div className="flex items-start gap-2 mt-2">
          <BulbOutlined className="text-yellow-600 dark:text-yellow-400 text-sm mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <p className="font-medium mb-1">Tips para mejores resultados:</p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Menciona las secciones específicas que necesitas (header, hero, features, footer, etc.)</li>
              <li>Describe el tipo de contenido (texto, imágenes, formularios, botones, etc.)</li>
              <li>Indica el tono/estilo que buscas (formal, casual, moderno, elegante, etc.)</li>
              <li>Especifica funcionalidades interactivas si las necesitas (menú hamburguesa, carousel, modal, etc.)</li>
              <li>Menciona el tipo de negocio/proyecto para mejor contexto</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Configuración de estilo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Estilo de diseño
          </label>
          <select
            value={style}
            onChange={(e) => handleStyleChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          >
            <option value="modern">🚀 Moderno - Clean y contemporáneo</option>
            <option value="minimal">✨ Minimalista - Simple y elegante</option>
            <option value="classic">📚 Clásico - Tradicional y profesional</option>
            <option value="bold">💪 Atrevido - Vibrante y llamativo</option>
            <option value="elegant">👔 Elegante - Sofisticado y refinado</option>
            <option value="playful">🎨 Juguetón - Divertido y creativo</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Paleta de colores
          </label>
          <select
            value={colors}
            onChange={(e) => handleColorsChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
          >
            <option value="blue">🔵 Azul - Confianza y profesionalismo</option>
            <option value="green">🟢 Verde - Naturaleza y crecimiento</option>
            <option value="purple">🟣 Púrpura - Creatividad y lujo</option>
            <option value="orange">🟠 Naranja - Energía y entusiasmo</option>
            <option value="red">🔴 Rojo - Pasión y urgencia</option>
            <option value="pink">💗 Rosa - Moderno y amigable</option>
            <option value="neutral">⚪ Neutral - Minimalista y versátil</option>
            <option value="dark">⚫ Oscuro - Elegante y moderno</option>
          </select>
        </div>
      </div>

      {/* Aviso importante */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-500 p-4 rounded-r-lg">
        <div className="flex items-start gap-2">
          <InfoCircleOutlined className="text-blue-600 dark:text-blue-400 text-lg mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
              ¿Cuándo se genera mi sitio?
            </p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Tu sitio web se generará automáticamente después de confirmar el pago. 
              El proceso toma aproximadamente 2-3 minutos. Recibirás una notificación cuando esté listo 
              y podrás editarlo con nuestro editor visual desde tu dashboard.
            </p>
          </div>
        </div>
      </div>

      {/* Preview de lo que se va a generar */}
      {description && description.length > 20 && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <CheckCircleOutlined className="text-green-600 dark:text-green-400 text-lg mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-green-900 dark:text-green-300 mb-2">
                ✅ Listo para generar
              </p>
              <div className="text-xs text-green-800 dark:text-green-200 space-y-1">
                <p><strong>Descripción:</strong> {description.substring(0, 100)}{description.length > 100 ? '...' : ''}</p>
                <p><strong>Estilo:</strong> {style.charAt(0).toUpperCase() + style.slice(1)}</p>
                <p><strong>Colores:</strong> {colors.charAt(0).toUpperCase() + colors.slice(1)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITemplateGeneratorStep;